import { type Signal, signal, useSignalEffect } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import type { Tool } from "../clay.ts";
import { Icon } from "./icon.tsx";
import styles from "./tutorial.module.css";

interface TutorialStep {
  title: string;
  instruction: string;
  /**
   * The tool the step is practised with, and how many strokes with it it takes. A step
   * without one is kneaded freely, the done button pointed at.
   */
  tool?: Tool;
  strokes?: number;
  /** Only strokes begun through the hole count, not strokes on the clay surface. */
  hole?: boolean;
  /** Where the step works on the clay, and where it draws the clay out to (see TutorialMark). */
  marks?: TutorialMark[];
  /**
   * The brush radii (unscaled, as the marks) that work the step as it should. A stroke with a
   * brush out of them is not counted, and the card asks for another size: the brush the step
   * is likely begun with is out of them on purpose, so a first try shows what the size is for.
   */
  brush?: {
    min?: number;
    max?: number;
    /** What goes wrong with a brush out of the sizes, as the card says it. */
    why: string;
  };
}

/**
 * A place the step works, in the target's unscaled units: marked on the clay where it lies
 * toward `from` from the middle, with an arrow on out to `to` while the clay falls short of it.
 */
export interface TutorialMark {
  from: [number, number, number];
  to?: [number, number, number];
}

interface TutorialLesson {
  title: string;
  steps: TutorialStep[];
}

export interface TutorialSession {
  lessonId: string;
  step: number;
  /** Strokes counted toward each step's practice, kept as the steps are gone back and on. */
  practiced: number[];
  /**
   * A stroke was made with a brush out of the step's sizes: the undo button and the brush
   * slider are pointed at, each until it is used, the slider with the way it should go.
   */
  undoPending: boolean;
  holePending: boolean;
  brushPending: "smaller" | "bigger" | null;
}

const LESSONS: Record<string, TutorialLesson> = {
  "cafe-mug": {
    title: "マグの穴のこね方",
    steps: [
      {
        title: "穴の中から広げよう",
        instruction:
          "穴は粘土を貫いて、向こうが見える所だよ。背景をドラッグして穴が見える向きに回そう。ろくろを切り、穴の中を「押す」で短く押してみよう。表面を押すとへこみ、穴の中を押すと穴が広がるよ。",
        tool: "push",
        strokes: 1,
        hole: true,
      },
      {
        title: "穴を少し狭めよう",
        instruction:
          "今度は穴の中を「盛る」で短く押してみよう。穴のまわりに土が盛られて、穴が狭くなるよ。変わりすぎたら「戻す」でやり直せるよ。",
        tool: "inflate",
        strokes: 1,
        hole: true,
      },
      {
        title: "穴を動かそう",
        instruction:
          "穴の中を「つまむ」でつかんで、少し横へ動かしてみよう。動かせないときは「戻す」で前の変形を戻して試そう。持ち手の穴と、まわりの土の厚みを見比べるのがコツだよ。",
        tool: "grab",
        strokes: 1,
        hole: true,
      },
      {
        title: "持ち手を仕上げよう",
        instruction:
          "穴と表面で道具の効き方が変わることが分かったね。見本と見比べて、丸い胴体と持ち手を整えよう。いい形になったら「できた！」で確認してね。",
      },
    ],
  },
  "jomonyan-replica": {
    title: "ねこ頭形土製品のこね方",
    steps: [
      {
        title: "二つのお耳をつまもう",
        instruction:
          "下の「つまむ」で、頭の上の光る輪をつかんで、矢印の先まで引っ張ってみよう。対称を使うと一度に作れるよ。",
        tool: "grab",
        strokes: 2,
        marks: [
          { from: [-0.55, 0.42, 0.3], to: [-0.52, 0.58, 0.1] },
          { from: [0.55, 0.42, 0.3], to: [0.52, 0.58, 0.1] },
        ],
        brush: { max: 0.2, why: "ブラシが大きいと、まわりの土までまとめて動いちゃうんだ。" },
      },
      {
        title: "丸い頬を盛ろう",
        instruction:
          "頬は顔の真横にあるよ。まわりの背景をドラッグしてカメラを横に回してから、「盛る」で光る輪を短く押してふくらませよう。長く押すと風船みたいになっちゃうから、少しずつね。",
        tool: "inflate",
        strokes: 2,
        marks: [{ from: [-0.8, -0.18, 0] }, { from: [0.8, -0.18, 0] }],
        brush: { min: 0.22, why: "ブラシが小さいと、そこだけぽこっとふくらんじゃうんだ。" },
      },
      {
        title: "輪郭をならそう",
        instruction:
          "ぽこっと出たほっぺを「ならす」でお顔になじませて、丸い輪郭にしよう。光る輪の上を、押したままくるくる長めにこするのがコツだよ。細かな模様や目鼻はなくても、ねこらしい形になるよ。",
        tool: "smooth",
        strokes: 2,
        marks: [{ from: [-0.75, -0.15, 0.35] }, { from: [0.75, -0.15, 0.35] }],
        brush: {
          min: 0.35,
          why: "ブラシが小さいと、ほっぺのほんの一部しかならせないんだ。",
        },
      },
      {
        title: "仕上げて提出しよう",
        instruction:
          "あとは自由にこねて、見本に近づけてみよう。いい形になったら、右下の「できた！」で提出してね。「納品する」を押すまでは、何度でもこね直せるよ。",
      },
    ],
  },
};

const TOOL_LABEL: Partial<Record<Tool, string>> = {
  grab: "つまむ",
  push: "押す",
  inflate: "盛る",
  smooth: "ならす",
};

/** The active guided lesson; a lesson is shown over the clay while it is worked. */
export const tutorial = signal<TutorialSession | null>(null);

/** Begins a lesson from its first step. */
export function startTutorial(lessonId: string): boolean {
  const lesson = LESSONS[lessonId];
  if (!lesson) {
    tutorial.value = null;
    return false;
  }
  tutorial.value = {
    lessonId,
    step: 0,
    practiced: [],
    undoPending: false,
    holePending: false,
    brushPending: null,
  };
  return true;
}

export function tutorialTitle(lessonId: string): string {
  return LESSONS[lessonId]?.title ?? "こね方";
}

export function hasTutorial(lessonId: string): boolean {
  return lessonId in LESSONS;
}

function currentStep(): TutorialStep | null {
  const session = tutorial.value;
  return session ? LESSONS[session.lessonId]?.steps[session.step] ?? null : null;
}

export function tutorialTool(): Tool | null {
  return currentStep()?.tool ?? null;
}

/** The lesson is at a step kneaded freely: the done button is pointed at. */
export function tutorialFinishing(): boolean {
  const step = currentStep();
  return !!step && !step.tool;
}

/** Where the current step works and how big a brush it takes (see TutorialStep). */
export function tutorialGuide(): Pick<TutorialStep, "marks" | "brush"> | null {
  return currentStep();
}

/** The undo button is pointed at, after a stroke with a brush out of size, until it is used. */
export function undoNudged(): boolean {
  return tutorial.value?.undoPending ?? false;
}

/**
 * The way the brush slider is pointed to go, after a stroke with a brush out of size, until
 * it is moved.
 */
export function brushNudged(): TutorialSession["brushPending"] {
  return tutorial.value?.brushPending ?? null;
}

/** Strokes counted toward the current step's practice. */
function practicedNow(session: TutorialSession): number {
  return session.practiced[session.step] ?? 0;
}

export function advanceTutorial(): void {
  const session = tutorial.value;
  const lesson = session && LESSONS[session.lessonId];
  const step = lesson && session ? lesson.steps[session.step] : null;
  if (!session || !lesson || !step || practicedNow(session) < (step.strokes ?? 0)) return;
  if (session.step === lesson.steps.length - 1) {
    tutorial.value = null;
    return;
  }
  tutorial.value = {
    ...session,
    step: session.step + 1,
    undoPending: false,
    brushPending: null,
    holePending: false,
  };
}

/** Back to the step before, as practised as it was left. */
export function backTutorial(): void {
  const session = tutorial.value;
  if (!session || session.step === 0) return;
  tutorial.value = {
    ...session,
    step: session.step - 1,
    undoPending: false,
    brushPending: null,
    holePending: false,
  };
}

export function skipTutorial(): void {
  tutorial.value = null;
}

/**
 * The lesson's card. A stroke with the step's tool counts toward its practice, unless its
 * brush (`strokeBrush`, unscaled as the step's) was out of the sizes the step takes: then the
 * card asks for it to be undone and for another `brush`, pointing at each until it is used.
 */
export function TutorialCoach(
  { strokes, strokeBrush, strokeTool, strokeOnHole, brush, selectTool }: {
    strokes: Signal<number>;
    strokeBrush: Signal<number>;
    brush: Signal<number>;
    strokeTool: Signal<Tool>;
    strokeOnHole: Signal<boolean>;
    selectTool: (tool: Tool) => void;
  },
) {
  const previousStrokes = useRef(strokes.peek());
  useSignalEffect(() => {
    const count = strokes.value;
    const selected = strokeTool.peek();
    const session = tutorial.value;
    const step = currentStep();
    const difference = count - previousStrokes.current;
    previousStrokes.current = count;
    if (!session || !step || difference === 0) return;
    if (difference < 0) {
      if (session.undoPending) tutorial.value = { ...session, undoPending: false };
      return;
    }
    if (!step.tool || step.tool !== selected) return;
    if (step.hole && !strokeOnHole.peek()) {
      tutorial.value = { ...session, holePending: true };
      return;
    }
    const size = strokeBrush.peek(), { min = 0, max = Infinity } = step.brush ?? {};
    if (size < min || size > max) {
      const brushPending = size > max ? "smaller" : "bigger";
      tutorial.value = { ...session, undoPending: true, brushPending };
      return;
    }
    const now = practicedNow(session), next = Math.min(step.strokes ?? 0, now + difference);
    if (next !== now || session.undoPending || session.brushPending || session.holePending) {
      const practiced = [...session.practiced];
      practiced[session.step] = next;
      tutorial.value = {
        ...session,
        practiced,
        undoPending: false,
        brushPending: null,
        holePending: false,
      };
    }
  });
  const previousBrush = useRef(brush.peek());
  useSignalEffect(() => {
    const size = brush.value;
    if (size === previousBrush.current) return;
    previousBrush.current = size;
    const session = tutorial.peek();
    if (session?.brushPending) tutorial.value = { ...session, brushPending: null };
  });

  const session = tutorial.value;
  const lesson = session && LESSONS[session.lessonId];
  const step = lesson && session ? lesson.steps[session.step] : null;
  useEffect(() => {
    if (step?.tool) selectTool(step.tool);
  }, [session?.lessonId, session?.step, selectTool]);
  useEffect(() => {
    if (!session) return;
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") skipTutorial();
    };
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, [session?.lessonId]);
  // On small screens the card covers the clay: it shrinks into a button where it was once the
  // clay (or the view round it) is touched, and opens out again from it, or by itself with
  // advice to read or once the step is practised.
  const [away, setAway] = useState(false);
  useEffect(() => {
    if (!session) return;
    const slide = (e: PointerEvent) => {
      if (e.target instanceof HTMLCanvasElement) setAway(true);
    };
    addEventListener("pointerdown", slide);
    return () => removeEventListener("pointerdown", slide);
  }, [session?.lessonId]);
  const advice = session && step ? adviceOf(session, step) : null;
  const practised = !!step?.strokes && practicedNow(session!) >= step.strokes;
  useEffect(() => {
    if (advice || practised) setAway(false);
  }, [advice, practised]);

  if (!session || !lesson || !step) return null;
  const done = practicedNow(session);
  const ready = done >= (step.strokes ?? 0);
  const last = session.step === lesson.steps.length - 1;
  return (
    <div class={`${styles.coach} ${away ? styles.away : ""}`}>
      <button
        type="button"
        class={styles.reopen}
        aria-label={`説明を出す（${step.title}）`}
        aria-expanded={!away}
        onClick={() => setAway(false)}
      >
        <Icon name={step.tool ?? "paw"} />
        <b>{session.step + 1}/{lesson.steps.length}</b>
        <span>{step.title}</span>
      </button>
      <section
        class={`panel ${styles.card}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tutorial-title"
      >
        <div class={styles.heading}>
          <span>{lesson.title}</span>
          <span class={styles.page}>
            {session.step + 1} / {lesson.steps.length}
            {
              // On the last step, going on ends the lesson as skipping would.
              !last && (
                <button type="button" class={styles.skip} onClick={skipTutorial}>
                  スキップ
                </button>
              )
            }
          </span>
        </div>
        <h2 id="tutorial-title">{step.title}</h2>
        <p>{step.instruction}</p>
        {step.tool && (
          <div class={styles.practice} aria-live="polite">
            <Icon name={step.tool} />
            <span>
              「{TOOL_LABEL[step.tool] ?? step.tool}」でこねる <b>{done}/{step.strokes} 手</b>
            </span>
          </div>
        )}
        {advice && <p class={styles.advice} role="status">{advice}</p>}
        <div class={styles.actions}>
          {session.step > 0 && <button type="button" onClick={backTutorial}>戻る</button>}
          <button type="button" disabled={!ready} onClick={advanceTutorial}>
            {last ? "作業を続ける" : "次へ"}
          </button>
        </div>
      </section>
    </div>
  );
}

/** How the brush is to be changed, and why, after a stroke with a brush out of size. */
const BRUSH_ADVICE = {
  smaller: { way: "左", size: "小さく" },
  bigger: { way: "右", size: "大きく" },
};

/**
 * What the card asks for after a stroke with a brush out of the sizes `step` takes, while it
 * is still to be done.
 */
function adviceOf(
  { undoPending, brushPending, holePending }: TutorialSession,
  step: TutorialStep,
): string | null {
  if (holePending) {
    return "今の操作は粘土の表面に効いたよ。ろくろを切り、視点を回して向こうが見える穴の中から、もう一度試そう。";
  }
  if (undoPending && brushPending) {
    const { size } = BRUSH_ADVICE[brushPending];
    const tool = step.tool && (TOOL_LABEL[step.tool] ?? step.tool);
    return `${step.brush?.why}左下の「戻す」で一手前に戻して、下のスライダーでブラシを${size}してから、もう一度「${tool}」でやってみよう。`;
  }
  if (undoPending) return "いいね！ 次は左下の「戻す」で、さっきの一手を戻そう。";
  if (brushPending) {
    const { way, size } = BRUSH_ADVICE[brushPending];
    return `戻せたね！ 次は下のスライダーを${way}に動かして、ブラシを${size}しよう。`;
  }
  return null;
}
